<!--
  BlackPatientAddModal.vue
  블랙 환자 추가 모달 컴포넌트
  
  기능:
  - Excel 업로드를 통한 블랙 환자 일괄 추가
  - 단건 블랙 환자 추가
  - 병원별 블랙 환자 일괄 추가
  
  Props:
  - visible: 모달 표시 여부
  - api: API 인스턴스
  
  Events:
  - close: 모달 닫기
  - success: 성공 시 데이터 새로고침 요청
-->
<template>
  <a-modal
    :open="visible"
    :confirm-loading="state.modalLoading"
    :footer="null"
    @cancel="handleCancel"
    width="800px"
  >
    <template #title>
      <div class="modal-title">블랙 환자 추가</div>
    </template>
    
    <div class="modal-content">
      <!-- 탭 메뉴 -->
      <a-tabs v-model:activeKey="state.activeTab" @change="handleTabChange">
        <a-tab-pane key="single" tab="단건 추가">
          <div class="tab-content">
            <div class="form-container">
              <div class="form-row">
                <div class="form-group">
                  <label for="patientName">환자명</label>
                  <a-input
                    id="patientName"
                    v-model:value="singleForm.patientName"
                    placeholder="환자명을 입력하세요"
                  />
                </div>
                
                <div class="form-group">
                  <label for="phoneNumber">전화번호</label>
                  <a-input
                    id="phoneNumber"
                    v-model:value="singleForm.phoneNumber"
                    placeholder="전화번호를 입력하세요 (예: 010-1234-5678)"
                    @input="handlePhoneNumberInput"
                  />
                </div>
              </div>
              
              <div class="form-actions">
                <a-button 
                  type="primary" 
                  class="yellow-button"
                  @click="addSinglePatient" 
                  :loading="state.singleLoading"
                  :disabled="!canAddSingle"
                >
                  <i class="fa fa-plus" style="margin-right: 5px"></i>블랙 환자 추가
                </a-button>
                
                <a-button @click="resetSingleForm">
                  <i class="fa fa-refresh" style="margin-right: 5px"></i>초기화
                </a-button>
              </div>
            </div>
          </div>
        </a-tab-pane>
        
        <a-tab-pane key="excel" tab="Excel 업로드">
          <div class="tab-content">
            <div class="upload-section">
              <div class="upload-info">
                <i class="fa fa-info-circle" style="color: #1890ff; margin-right: 8px"></i>
                <span>Excel 파일을 선택하면 자동으로 업로드됩니다.</span>
              </div>
              
              <a-upload
                :file-list="fileList"
                :before-upload="beforeUpload"
                @remove="handleRemove"
                accept=".xlsx,.xls"
                :disabled="state.uploadLoading"
              >
                <a-button :loading="state.uploadLoading" :disabled="state.uploadLoading">
                  <i class="fa fa-upload" style="margin-right: 5px"></i>
                  {{ state.uploadLoading ? '업로드 중...' : 'Excel 파일 선택' }}
                </a-button>
              </a-upload>
              
              <!-- 업로드 진행 상태 표시 -->
              <div v-if="state.uploadLoading" class="upload-progress">
                <a-spin size="small" />
                <span style="margin-left: 8px;">파일을 업로드하고 있습니다...</span>
              </div>
            </div>
          </div>
        </a-tab-pane>
        
        <a-tab-pane key="hospital" tab="병원별 일괄 추가">
          <div class="tab-content">
            <div class="form-container">
              <div class="form-group">
                <label for="hospitalSearch">병원 검색</label>
                <div class="search-input-group">
                  <a-input
                    id="hospitalSearch"
                    v-model:value="hospitalForm.searchKeyword"
                    placeholder="병원명 또는 사업자번호를 입력하세요"
                    @pressEnter="searchHospital"
                  />
                  <a-button 
                    type="primary" 
                    @click="searchHospital"
                    :loading="state.hospitalSearchLoading"
                  >
                    <i class="fa fa-search"></i>
                  </a-button>
                </div>
              </div>
              
              <!-- 병원 검색 결과 -->
              <div v-if="hospitalList.length > 0" class="hospital-results">
                <h4>검색 결과</h4>
                <div class="hospital-list">
                  <div 
                    v-for="hospital in hospitalList" 
                    :key="hospital._id || hospital.id"
                    class="hospital-item"
                    :class="{ 'selected': isHospitalSelected(hospital) }"
                    @click="selectHospital(hospital)"
                  >
                    <div class="hospital-info">
                      <div class="hospital-name">{{ hospital.name }}</div>
                      <div class="hospital-details">
                        <span>사업자번호: {{ hospital.businessNumber }}</span>
                        <span>환자 수: {{ hospital.patientCount }}명</span>
                      </div>
                    </div>
                    <i class="fa fa-check-circle" v-if="isHospitalSelected(hospital)"></i>
                  </div>
                </div>
                
                <div class="form-actions" v-if="selectedHospital">
                  <a-button 
                    type="primary" 
                    class="yellow-button"
                    @click="addHospitalPatients" 
                    :loading="state.hospitalLoading"
                  >
                    <i class="fa fa-users" style="margin-right: 5px"></i>
                    {{ selectedHospital.name }} 환자 일괄 추가 ({{ selectedHospital.patientCount }}명)
                  </a-button>
                </div>
              </div>
            </div>
          </div>
        </a-tab-pane>
      </a-tabs>
      
      <div class="modal-footer">
        <a-button @click="handleCancel">취소</a-button>
      </div>
    </div>
    
    <!-- 에러 결과 모달 -->
    <a-modal
      :open="errorModal.visible"
      :footer="null"
      @cancel="closeErrorModal"
      width="800px"
      title="처리 결과"
    >
      <div class="error-modal-content">
        <!-- 결과 요약 -->
        <div class="result-summary">
          <div class="summary-item success">
            <i class="fa fa-check-circle"></i>
            <span>신규: {{ errorModal.new }}건</span>
          </div>
          <div class="summary-item duplicate">
            <i class="fa fa-times-circle"></i>
            <span>중복: {{ errorModal.duplicate }}건</span>
          </div>
          <div class="summary-item failed">
            <i class="fa fa-times-circle"></i>
            <span>실패: {{ errorModal.failedCount }}건</span>
          </div>
          <div class="summary-item total">
            <i class="fa fa-list"></i>
            <span>전체: {{ errorModal.totalCount }}건</span>
          </div>
        </div>
        
        <!-- 실패 목록 테이블 -->
        <div v-if="errorModal.errors.length > 0" class="error-table-container">
          <h4 style="margin-left: 10px;">실패 목록</h4>
          <a-table
            :columns="errorTableColumns"
            :data-source="errorModal.errors"
            :pagination="{
              pageSize: 10,
              showSizeChanger: false,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} / 총 ${total}개`
            }"
            size="small"
            row-key="index"
          >
            <template #bodyCell="{ column, record, index }">
              <template v-if="column.key === 'index'">
                {{ index + 1 }}
              </template>
              <template v-else-if="column.key === 'error'">
                <span class="error-message">{{ record.message }}</span>
              </template>
            </template>
          </a-table>
        </div>
      </div>
    </a-modal>
  </a-modal>
</template>

<script>
import { ref, reactive, computed, watch } from 'vue';
import { message } from 'ant-design-vue';

// 상수 정의
const FILE_TYPES = {
  EXCEL_NEW: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  EXCEL_OLD: 'application/vnd.ms-excel'
};

const MAX_FILE_SIZE = 10; // MB

export default {
  name: 'BlackPatientAddModal',
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    api: {
      type: Object,
      default: null
    },
    blackPatientList: {
      type: Array,
      default: () => []
    }
  },
  emits: ['close', 'success'],
  setup(props, { emit }) {
    // 반응형 상태 정의
    const state = reactive({
      activeTab: 'single',
      modalLoading: false,
      uploadLoading: false,
      singleLoading: false,
      hospitalSearchLoading: false,
      hospitalLoading: false
    });

    const fileList = ref([]);
    const hospitalList = ref([]);
    const selectedHospital = ref(null);
    
    // 에러 모달 상태
    const errorModal = reactive({
      visible: false,
      successCount: 0,
      failedCount: 0,
      totalCount: 0,
      new: 0,
      duplicate: 0,
      errors: []
    });
    
    // 폼 데이터
    const singleForm = reactive({
      patientName: '',
      phoneNumber: ''
    });
    
    const hospitalForm = reactive({
      searchKeyword: ''
    });
    
    // 에러 테이블 컬럼 정의
    const errorTableColumns = [
      {
        title: '번호',
        dataIndex: 'index',
        key: 'index',
        width: 80,
        align: 'center'
      },
      {
        title: '오류 내용',
        dataIndex: 'message',
        key: 'error',
        ellipsis: true
      }
    ];
    
    // 전화번호 관련 유틸리티 함수들
    const phoneUtils = {
      // 전화번호 포맷팅 (표시용)
      format: (phoneNumber) => {
        if (!phoneNumber) return '';
        
        const numbers = phoneNumber.replace(/\D/g, '');
        
        // 휴대폰 번호 (11자리)
        if (numbers.length === 11 && /^01[0-9]/.test(numbers)) {
          return `${numbers.slice(0, 3)}-${numbers.slice(3, 7)}-${numbers.slice(7)}`;
        }
        
        // 일반 전화번호
        if (numbers.length >= 9) {
          if (numbers.length === 9) {
            return `${numbers.slice(0, 2)}-${numbers.slice(2, 5)}-${numbers.slice(5)}`;
          } else if (numbers.length === 10) {
            if (numbers.startsWith('02')) {
              return `${numbers.slice(0, 2)}-${numbers.slice(2, 6)}-${numbers.slice(6)}`;
            } else {
              return `${numbers.slice(0, 3)}-${numbers.slice(3, 6)}-${numbers.slice(6)}`;
            }
          }
        }
        
        return phoneNumber;
      },
      
      // 하이픈 제거 (저장용)
      clean: (phoneNumber) => {
        if (!phoneNumber) return '';
        return phoneNumber.replace(/\D/g, '');
      },
      
      // 전화번호 유효성 검사
      validate: (phoneNumber) => {
        const numbers = phoneUtils.clean(phoneNumber);
        const mobilePattern = /^01[0-9]\d{8}$/;
        const landlinePattern = /^(02|0[3-9]\d)\d{6,8}$/;
        return mobilePattern.test(numbers) || landlinePattern.test(numbers);
      }
    };
    
    // 단건 추가 가능 여부
    const canAddSingle = computed(() => {
      return singleForm.patientName.trim() && singleForm.phoneNumber.trim();
    });
    
    // 공통 에러 처리 함수 - 개선된 버전
    const handleError = (error, defaultMessage) => {
      console.error('Error occurred:', error);
      
      let errorMessage = defaultMessage;
      
      // MongoDB 중복 키 에러 특별 처리
      if (error.response?.data?.error === 'MongoServerError' && 
          error.response?.data?.message?.includes('E11000 duplicate key error')) {
        if (error.response.data.message.includes('phoneNumber')) {
          errorMessage = '이미 등록된 전화번호입니다. 다른 전화번호를 입력해주세요.';
        } else {
          errorMessage = '중복된 데이터가 존재합니다.';
        }
      } else if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      message.error(errorMessage);
      return errorMessage;
    };

    // 탭 변경 핸들러
    const handleTabChange = (key) => {
      state.activeTab = key;
      
      // 병원 탭이 아닌 다른 탭으로 변경할 때 병원 관련 데이터 초기화
      if (key !== 'hospital') {
        resetHospitalSelection();
        hospitalList.value = [];
        hospitalForm.searchKeyword = '';
      }
    };
    
    // 모달 닫기
    const handleCancel = () => {
      resetModalData();
      emit('close');
    };
    
    // 모달 데이터 초기화
    const resetModalData = () => {
      fileList.value = [];
      resetSingleForm();
      hospitalList.value = [];
      selectedHospital.value = null;
      hospitalForm.searchKeyword = '';
      state.activeTab = 'single';
      state.uploadLoading = false; // 업로드 상태 초기화
      closeErrorModal(); // 에러 모달 닫기
    };
    
    // 에러 모달 표시
    const showErrorModal = (successCount, failedCount, errors, newCount = 0, duplicateCount = 0) => {
      // 에러 문자열을 테이블 데이터로 변환
      const errorData = errors.map((errorStr, index) => {
        return {
          index: index + 1,
          message: errorStr
        };
      });
      
      errorModal.successCount = successCount;
      errorModal.failedCount = failedCount;
      errorModal.totalCount = successCount + failedCount;
      errorModal.new = newCount;
      errorModal.duplicate = duplicateCount;
      errorModal.errors = errorData;
      errorModal.visible = true;
    };
    
    // 에러 모달 닫기
    const closeErrorModal = () => {
      errorModal.visible = false;
      errorModal.successCount = 0;
      errorModal.failedCount = 0;
      errorModal.totalCount = 0;
      errorModal.new = 0;
      errorModal.duplicate = 0;
      errorModal.errors = [];
    };
    
    // 파일 유효성 검사
    const validateFile = (file) => {
      // 파일 존재 확인
      if (!file) {
        message.error('파일을 선택해주세요.');
        return false;
      }
      
      // 파일 확장자 검사
      const fileName = file.name.toLowerCase();
      const validExtensions = ['.xlsx', '.xls'];
      const hasValidExtension = validExtensions.some(ext => fileName.endsWith(ext));
      
      // MIME 타입 검사
      const isExcel = file.type === FILE_TYPES.EXCEL_NEW || file.type === FILE_TYPES.EXCEL_OLD;
      
      if (!hasValidExtension || !isExcel) {
        message.error('Excel 파일(.xlsx, .xls)만 업로드 가능합니다.');
        return false;
      }
      
      // 파일 크기 검사
      const fileSizeMB = file.size / 1024 / 1024;
      if (fileSizeMB > MAX_FILE_SIZE) {
        message.error(`파일 크기는 ${MAX_FILE_SIZE}MB 이하여야 합니다. (현재: ${fileSizeMB.toFixed(2)}MB)`);
        return false;
      }
      
      // 파일명 길이 검사 (Windows 파일시스템 제한 고려)
      if (file.name.length > 255) {
        message.error('파일명이 너무 깁니다. 255자 이하로 해주세요.');
        return false;
      }
      
      return true;
    };
    
    // Excel 업로드 전 처리 및 자동 업로드
    const beforeUpload = async (file) => {
      if (!validateFile(file)) return false;
      
      if (!props.api) {
        message.error('API가 초기화되지 않았습니다.');
        return false;
      }
      
      fileList.value = [file];
      state.uploadLoading = true;
      
      try {
        await props.api.uploadBlacklistPatients(
          file,
          (response) => {
            const data = response.data?.data;
            if (data) {
              const successCount = data.success || 0;
              const failedCount = data.failed || 0;
              const newCount = data.new || 0;
              const duplicateCount = data.duplicate || 0;
              const errors = data?.errors || [];
              
              showErrorModal(successCount, failedCount, errors, newCount, duplicateCount);
              emit('success');
            } else {
              message.success('Excel 파일이 성공적으로 업로드되었습니다.');
              emit('success'); // 부모 컴포넌트에 성공 알림
              handleCancel(); // 모달 닫기
            }
            fileList.value = [];
          },
          (error) => {
            handleError(error, 'Excel 업로드 중 오류가 발생했습니다.');
            fileList.value = []; // 에러 시 파일 목록 초기화
          }
        );
      } catch (error) {
        handleError(error, 'Excel 업로드 중 오류가 발생했습니다.');
        fileList.value = []; // 예외 시 파일 목록 초기화
      } finally {
        state.uploadLoading = false;
      }
      
      return false; // Ant Design Upload 컴포넌트의 기본 업로드 방지
    };
    
    // 파일 제거
    const handleRemove = () => {
      fileList.value = [];
      state.uploadLoading = false; // 업로드 상태 초기화
    };
    
    // 전화번호 입력 핸들러
    const handlePhoneNumberInput = (event) => {
      const value = event.target ? event.target.value : event;
      singleForm.phoneNumber = phoneUtils.format(value);
    };
    
    // 단건 환자 추가 - 수정된 버전
    const addSinglePatient = async () => {
      if (!canAddSingle.value) {
        message.warning('환자명과 전화번호를 모두 입력해주세요.');
        return;
      }
      
      // 전화번호 유효성 검사
      if (!phoneUtils.validate(singleForm.phoneNumber)) {
        message.warning('올바른 전화번호 형식을 입력해주세요.');
        return;
      }
      
      if (!props.api) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      state.singleLoading = true;
      
      try {
        // 하이픈 제거된 전화번호로 중복 검증 (현재 로드된 데이터에서만)
        const cleanPhoneNumber = phoneUtils.clean(singleForm.phoneNumber);
        const isDuplicate = props.blackPatientList.some(patient => 
          phoneUtils.clean(patient.phoneNumber) === cleanPhoneNumber
        );
        
        if (isDuplicate) {
          message.warning('이미 등록된 전화번호입니다.');
          return;
        }
        
        // API 전송용 데이터 (하이픈 제거)
        const patientData = {
          ...singleForm,
          phoneNumber: cleanPhoneNumber,
        };
        
        await props.api.addBlacklistPatient(
          patientData,
          (response) => {
            message.success('블랙 환자가 성공적으로 추가되었습니다.');
            resetSingleForm();
            emit('success'); // 부모 컴포넌트에 성공 알림
            handleCancel(); // 모달 닫기
          },
          (error) => {
            handleError(error, '블랙 환자 추가 중 오류가 발생했습니다.');
          }
        );
      } catch (error) {
        handleError(error, '블랙 환자 추가 중 오류가 발생했습니다.');
      } finally {
        state.singleLoading = false;
      }
    };
    
    // 단건 폼 초기화
    const resetSingleForm = () => {
      singleForm.patientName = '';
      singleForm.phoneNumber = '';
    };
    
    // 병원 검색
    const searchHospital = async () => {
      if (!hospitalForm.searchKeyword.trim()) {
        message.warning('병원명 또는 사업자번호를 입력해주세요.');
        return;
      }
      
      if (!props.api) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      state.hospitalSearchLoading = true;
      
      try {
        await props.api.searchHospitalsForBlacklist(
          hospitalForm.searchKeyword,
          (response) => {
            hospitalList.value = response.data?.data || [];
            selectedHospital.value = null; // 검색 시 선택 초기화
            
            if (hospitalList.value.length === 0) {
              message.info('검색 결과가 없습니다.');
            }
          },
          (error) => {
            handleError(error, '병원 검색 중 오류가 발생했습니다.');
            hospitalList.value = [];
            selectedHospital.value = null; // 에러 시에도 선택 초기화
          }
        );
      } catch (error) {
        hospitalList.value = [];
        selectedHospital.value = null; // 예외 시에도 선택 초기화
      } finally {
        state.hospitalSearchLoading = false;
      }
    };
    
    // 병원 선택
    const selectHospital = (hospital) => {
      // 이미 선택된 병원을 다시 클릭하면 선택 해제
      if (isHospitalSelected(hospital)) {
        selectedHospital.value = null;
      } else {
        selectedHospital.value = hospital;
      }
    };
    
    // 병원 환자 일괄 추가
    const addHospitalPatients = async () => {
      if (!selectedHospital.value) {
        message.warning('병원을 선택해주세요.');
        return;
      }
      
      if (!props.api) {
        message.error('API가 초기화되지 않았습니다.');
        return;
      }
      
      state.hospitalLoading = true;
      
      try {
        await props.api.addHospitalBlacklistPatients(
          {
            hospitalId: selectedHospital.value._id || selectedHospital.value.id,
          },
          (response) => {
            const data = response.data?.data;
            console.log('addHospitalPatients.data :>> ', data);
            
            const addedCount = data?.success || 0;
            const failedCount = data?.failed || 0;
            const newCount = data?.new || 0;
            const duplicateCount = data?.duplicate || 0;
            const errors = data?.errors || [];

            showErrorModal(addedCount, failedCount, errors, newCount, duplicateCount);
            emit('success');
            
            hospitalList.value = [];
            selectedHospital.value = null;
            hospitalForm.searchKeyword = '';
          },
          (error) => {
            handleError(error, '병원 환자 일괄 추가 중 오류가 발생했습니다.');
          }
        );
      } catch (error) {
        // 에러는 이미 handleError에서 처리됨
      } finally {
        state.hospitalLoading = false;
      }
    };
    
    // 병원 선택 여부 확인
    const isHospitalSelected = (hospital) => {
      if (!selectedHospital.value || !hospital) {
        return false;
      }
      
      // ID 기반 비교 (우선순위: _id > id)
      const selectedId = selectedHospital.value._id || selectedHospital.value.id;
      const hospitalId = hospital._id || hospital.id;
      
      return selectedId && hospitalId && selectedId === hospitalId;
    };
    
    // 병원 선택 초기화
    const resetHospitalSelection = () => {
      selectedHospital.value = null;
    };
    
    // visible prop 변경 감지
    watch(() => props.visible, (newVisible) => {
      if (newVisible) {
        resetModalData();
      }
    });
    
    return {
      state,
      fileList,
      hospitalList,
      selectedHospital,
      singleForm,
      hospitalForm,
      canAddSingle,
      phoneUtils,
      handleTabChange,
      handleCancel,
      beforeUpload,
      handleRemove,
      handlePhoneNumberInput,
      addSinglePatient,
      resetSingleForm,
      searchHospital,
      selectHospital,
      addHospitalPatients,
      isHospitalSelected,
      resetHospitalSelection,
      
      // 에러 모달 관련
      errorModal,
      errorTableColumns,
      showErrorModal,
      closeErrorModal
    };
  }
};
</script>

<style scoped lang="scss">
// 모달 스타일
.modal-title {
  font-size: 16px;
  font-weight: bold;
}

.modal-content {
  padding: 20px;
  border: none;
  background: #fff;
}

.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-top: 24px;
}

// 탭 컨테이너
:deep(.ant-tabs) {
  .ant-tabs-tab {
    font-weight: 500;
  }
  
  .ant-tabs-content-holder {
    padding: 0;
  }
}

.tab-content {
  padding: 20px;
}

// 업로드 섹션
.upload-section {
  .upload-info {
    display: flex;
    align-items: center;
    margin-bottom: 16px;
    padding: 12px;
    background-color: #f6f8ff;
    border-radius: 6px;
    border-left: 4px solid #1890ff;
    
    span {
      font-size: 14px;
      color: #333;
    }
  }
  
  .upload-progress {
    display: flex;
    align-items: center;
    margin-top: 16px;
    padding: 12px;
    background-color: #f6ffed;
    border-radius: 6px;
    border-left: 4px solid #52c41a;
    
    span {
      font-size: 14px;
      color: #389e0d;
    }
  }
  
  .upload-actions {
    margin-top: 16px;
  }
}

// 폼 스타일
.form-container {
  .form-row {
    display: flex;
    gap: 20px;
    margin-bottom: 20px;
  }
  
  .form-group {
    flex: 1;
    
    label {
      display: block;
      margin-bottom: 8px;
      font-weight: 500;
      font-size: 14px;
      color: #262626;
    }
    
    :deep(.ant-input),
    :deep(.ant-select) {
      border-radius: 6px;
    }
  }
  
  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
    margin-top: 24px;
    
    .ant-btn {
      min-width: 120px;
      height: 40px;
      font-size: 14px;
      display: flex;
      align-items: center;
      justify-content: center;
    }
  }
}

// 검색 입력 그룹
.search-input-group {
  display: flex;
  gap: 8px;
  
  .ant-input {
    flex: 1;
  }
}

// 병원 검색 결과
.hospital-results {
  margin-top: 24px;
  
  h4 {
    margin-bottom: 16px;
    font-size: 16px;
    font-weight: 600;
    color: #000;
  }
}

.hospital-list {
  border: 1px solid #f0f0f0;
  border-radius: 8px;
  overflow: hidden;
  margin-bottom: 20px;
}

.hospital-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid #f0f0f0;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:last-child {
    border-bottom: none;
  }
  
  &:hover {
    background-color: #f9f9f9;
  }
  
  &.selected {
    background-color: #e6f7ff;
    border-color: #1890ff;
  }
  
  .hospital-info {
    flex: 1;
    
    .hospital-name {
      font-size: 16px;
      font-weight: 600;
      color: #000;
      margin-bottom: 4px;
    }
    
    .hospital-details {
      display: flex;
      gap: 16px;
      font-size: 14px;
      color: #666;
    }
  }
  
  .fa-check-circle {
    color: #1890ff;
    font-size: 18px;
  }
}

// 버튼 스타일
:deep(.yellow-button) {
  background-color: #FFCC32 !important;
  border-color: #FFCC32 !important;
  color: #000 !important;
  
  &:hover {
    background-color: #E6B82E !important;
    border-color: #E6B82E !important;
  }
  
  &:disabled {
    background-color: #d9d9d9 !important;
    border-color: #d9d9d9 !important;
    color: rgba(0, 0, 0, 0.25) !important;
  }
}

// 에러 모달 스타일
.error-modal-content {
  .result-summary {
    display: flex;
    gap: 16px;
    margin-bottom: 24px;
    padding: 16px;
    background-color: #f9f9f9;
    border-radius: 8px;
    
    .summary-item {
      display: flex;
      align-items: center;
      gap: 8px;
      padding: 8px 12px;
      border-radius: 6px;
      font-weight: 500;
      
      &.success {
        background-color: #f6ffed;
        color: #389e0d;
        
        i {
          color: #52c41a;
        }
      }
      
      &.duplicate {
        background-color: #fff2f0;
        color: #d46b08;
        
        i {
          color: #d46b08;
        }
      }
      
      &.failed {
        background-color: #fff2f0;
        color: #cf1322;
        
        i {
          color: #ff4d4f;
        }
      }
      
      &.total {
        background-color: #f0f5ff;
        color: #1890ff;
        
        i {
          color: #1890ff;
        }
      }
    }
  }
  
  .error-table-container {
    h4 {
      margin-bottom: 16px;
      font-size: 16px;
      font-weight: 600;
      color: #262626;
    }
    
    :deep(.ant-table) {
      .error-message {
        color: #cf1322;
        font-size: 13px;
        line-height: 1.4;
      }
    }
  }
}

// 반응형 스타일
@media screen and (max-width: 768px) {
  .form-row {
    flex-direction: column;
    gap: 0;
  }
  
  .form-actions {
    flex-direction: column;
    
    .ant-btn {
      width: 100%;
    }
  }
  
  .hospital-item {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
    
    .fa-check-circle {
      align-self: flex-end;
    }
  }
}
</style> 